/**
 * Tipos e erro compartilhados do **certificado local do signatário**.
 *
 * ## O que este arquivo é hoje (D-116)
 *
 * Ele já foi o cliente HTTP do agente Java de `apps/local-agent` (porta 3311). Não é mais:
 * com o ARGINK Bridge como provider ÚNICO da Qualificada, o transporte para o Java saiu
 * inteiro do produto — `checkAgentHealth`, `listCerts`, `authorizeWithAgent`, `signWithAgent`
 * e o `fetch` com fallback de proxy deixaram de existir aqui. Nenhuma linha deste módulo
 * abre conexão com coisa nenhuma.
 *
 * O que ficou é o vocabulário que o resto do signer-ui já fala: `AgentCert` (o formato de
 * certificado que `CertList`, `lib/cpf.ts` e `SignPage` consomem) e `AgentError` (a classe
 * que `lib/errors.ts` sabe humanizar). `lib/bridge.ts` produz os dois a partir das respostas
 * do Bridge. Os nomes foram preservados de propósito: renomeá-los seria um refactor
 * cosmético atravessando sete arquivos, sem mudar nada do comportamento que a D-116 pede.
 *
 * O agente Java continua existindo em `apps/local-agent` como ferramenta interna
 * (workbench, diagnóstico, compatibilidade histórica) — fora de qualquer jornada de produto.
 */

export interface AgentCert {
  keyId: string;
  label: string;
  subject: string;
  issuer: string;
  notAfter: string;
  source: 'p12' | 'mscapi';
  certChainB64: string[];
}

/**
 * Corpo de erro estruturado que acompanha um `AgentError`.
 *
 * `error` é o código como o outro lado o classificou (para o Bridge, o `code` do protocolo
 * v1); `trace_id` é o identificador que o servidor devolveu, quando devolveu — `describeError`
 * prefere esse ao `traceId` da chamada. Todos os campos são opcionais e nunca assumidos
 * presentes.
 */
export interface AgentErrorBody {
  message?: string;
  error?: string;
  err_class?: string;
  trace_id?: string;
}

/**
 * `kind` distingue os cenários para os quais a UI tem mensagens próprias:
 *  - 'agent_unreachable': não foi possível falar com o programa local nesta máquina.
 *  - 'timeout': o programa local aceitou a conexão mas não respondeu a tempo — distinto
 *    do timeout da API (quem não respondeu foi a máquina do usuário, não o servidor).
 *  - 'origin_blocked': a origem desta página não está autorizada no programa local
 *    (allowlist do loopback, D-014) — não confundir com sessão/nonce expirado.
 *  - 'auth_denied': o usuário recusou no diálogo nativo.
 *  - 'consent_timeout': o diálogo nativo ABRIU e ninguém respondeu dentro da janela do
 *    Bridge. É o único kind cuja causa é a ausência de uma PESSOA, não uma falha de
 *    máquina: dizer "o Bridge não respondeu" aqui seria factualmente errado e esconderia
 *    do signatário a única ação que destrava a tela — responder à janela que já abriu.
 *  - 'response': qualquer outra recusa do programa local (certificado não encontrado,
 *    algoritmo não suportado, etc.).
 */
export type AgentErrorKind =
  | 'agent_unreachable'
  | 'timeout'
  | 'origin_blocked'
  | 'auth_denied'
  | 'consent_timeout'
  | 'response';

export class AgentError extends Error {
  readonly kind: AgentErrorKind;
  readonly status: number | null;
  readonly body: AgentErrorBody | null;
  readonly traceId: string;

  constructor(
    kind: AgentErrorKind,
    message: string,
    traceId: string,
    status: number | null = null,
    body: AgentErrorBody | null = null,
  ) {
    super(message);
    this.name = 'AgentError';
    this.kind = kind;
    this.traceId = traceId;
    this.status = status;
    this.body = body;
  }
}
